import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  InputGroup,
  Modal,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUser,
  FaCreditCard,
  FaUniversity,
  FaMobile,
  FaRupeeSign,
  FaShieldAlt,
} from "react-icons/fa";
import Swal from "sweetalert2";

import PPIDmtService from "../../../services/PPIDmtService";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import SideBar from "../../../components/Sidebar";

const AddBeneficiary = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { customerMobile, data } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    mobile_number: "",
    name: "",
    account_number: "",
    ifsc_code: "",
    bank_name: "",
    verify_account: false,
  });
  const [errors, setErrors] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Format input values
    let formattedValue = value;

    if (name === "account_number") {
      // Remove non-numeric characters for account number
      formattedValue = value.replace(/\D/g, "");
    } else if (name === "ifsc_code") {
      // Convert IFSC to uppercase
      formattedValue = value.toUpperCase();
    } else if (name === "mobile_number") {
      // Format mobile number
      formattedValue = value.replace(/\D/g, "").substring(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : formattedValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Mobile number validation
    if (!formData.mobile_number.trim()) {
      newErrors.mobile_number = "Mobile number is required";
    } else if (formData.mobile_number.length !== 10) {
      newErrors.mobile_number = "Mobile number must be 10 digits";
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Beneficiary name is required";
    } else if (formData.name.length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    }

    // Account number validation
    if (!formData.account_number.trim()) {
      newErrors.account_number = "Account number is required";
    } else if (
      formData.account_number.length < 9 ||
      formData.account_number.length > 18
    ) {
      newErrors.account_number = "Account number must be 9-18 digits";
    }

    // IFSC validation
    if (!formData.ifsc_code.trim()) {
      newErrors.ifsc_code = "IFSC code is required";
    } else if (formData.ifsc_code.length !== 11) {
      newErrors.ifsc_code = "IFSC must be 11 characters";
    }

    // Bank name validation
    if (!formData.bank_name.trim()) {
      newErrors.bank_name = "Bank name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fix all errors before submitting",
        timer: 2000,
      });
      return;
    }

    // Show confirmation modal if verification is enabled
    if (formData.verify_account) {
      setShowConfirmModal(true);
    } else {
      submitBeneficiary();
    }
  };

  // Submit beneficiary to API
  const submitBeneficiary = async () => {
    setLoading(true);

    try {
      const payload = {
        ...formData,
        // Ensure proper field names as expected by API
        beneficiary_mobile: formData.mobile_number,
        beneficiary_name: formData.name,
        account_number: formData.account_number,
        ifsc_code: formData.ifsc_code,
        bank_name: formData.bank_name,
      };

      const result = await PPIDmtService.addBeneficiary(
        data?.token,
        customerMobile,
        payload
      );

      if (!result.success) {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: result.message || "Request failed!",
        });
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: res.message || "Beneficiary added successfully",
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          navigate("/ppi-money-transfer/sender-home", {
            state: { customerMobile, data },
          });
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: res.message || "Something went wrong",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: error.response?.data?.message || error.message || "Network error",
      });
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  const handleGoBack = () => {
    navigate("/ppi-money-transfer/sender-home", {
      state: { customerMobile, data },
    });
  };

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />
        <div className="col-md-9 mb-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title pb-2">Add Beneficiary</h5>

              <Form onSubmit={handleSubmit}>
                {/* Mobile Number */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    <FaMobile className="me-2" /> Beneficiary Mobile Number
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>+91</InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="mobile_number"
                      value={formData.mobile_number}
                      onChange={handleChange}
                      placeholder="Enter 10-digit mobile number"
                      maxLength={10}
                      isInvalid={!!errors.mobile_number}
                      disabled={loading}
                    />
                  </InputGroup>
                  <Form.Control.Feedback type="invalid">
                    {errors.mobile_number}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Beneficiary Name */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    <FaUser className="me-2" /> Beneficiary Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter beneficiary full name"
                    isInvalid={!!errors.name}
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Account Number */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    <FaCreditCard className="me-2" /> Account Number
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="account_number"
                    value={formData.account_number}
                    onChange={handleChange}
                    placeholder="Enter account number"
                    maxLength={18}
                    isInvalid={!!errors.account_number}
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.account_number}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* IFSC Code */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    <FaUniversity className="me-2" /> IFSC Code
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="ifsc_code"
                    value={formData.ifsc_code}
                    onChange={handleChange}
                    placeholder="Enter 11-character IFSC code"
                    maxLength={11}
                    isInvalid={!!errors.ifsc_code}
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.ifsc_code}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Bank Name */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    <FaUniversity className="me-2" /> Bank Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleChange}
                    placeholder="Enter bank name"
                    isInvalid={!!errors.bank_name}
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.bank_name}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Verification Checkbox */}
                <Form.Group className="mb-4">
                  <Card className="border-warning">
                    <Card.Body>
                      <Form.Check
                        type="checkbox"
                        id="verify_account"
                        name="verify_account"
                        checked={formData.verify_account}
                        onChange={handleChange}
                        label={
                          <div>
                            <div className="d-flex align-items-center mb-1">
                              <FaShieldAlt className="me-2 text-warning" />
                              <strong>Verify Account Name</strong>
                            </div>
                            <p className="text-muted mb-0 small">
                              <FaRupeeSign className="me-1" />
                              ₹1 will be deposited + ₹2 charges (Total ₹3.54)
                              will be deducted from your wallet to verify
                              beneficiary account name.
                            </p>
                            <p className="text-success small mt-1">
                              <FaCheckCircle className="me-1" />
                              Recommended for secure transactions
                            </p>
                          </div>
                        }
                        disabled={loading}
                      />
                    </Card.Body>
                  </Card>
                </Form.Group>

                {/* Submit Button */}
                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <Button
                    variant="outline-secondary"
                    onClick={handleGoBack}
                    disabled={loading}
                  >
                    Back to Dashboard
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    className="px-4"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="me-2" />
                        Add Beneficiary
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </Row>

      {/* Confirmation Modal for Verification */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-warning">
          <Modal.Title className="text-warning">
            <FaExclamationTriangle className="me-2" />
            Confirm Account Verification
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="d-flex align-items-center">
            <FaExclamationTriangle className="me-2 flex-shrink-0" size={24} />
            <div>
              <strong>Account Verification Charge</strong>
              <p className="mb-0">
                ₹3.54 (₹1 deposit + ₹2 charges) will be deducted from your
                wallet for account verification.
              </p>
            </div>
          </Alert>
          <div className="text-center">
            <p className="mb-0">Do you want to proceed with verification?</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={submitBeneficiary}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Processing...
              </>
            ) : (
              "Yes, Verify & Add"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AddBeneficiary;
